import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Spinner, Alert } from 'react-bootstrap';
import apiClient from '../../../AppClient';
import { useAuth } from '../../../context/AuthContext';

const PaymentModal = ({ show, handleClose, suppliers, handleSubmit, mode, initialValues = null }) => {
  const { paymentMethods } = useAuth();
  const [formData, setFormData] = useState({
    supplier: '',
    installment: '',
    payment_method: paymentMethods[0]?.name || '',
    amount: '',
    notes: ''
  });

  const [validated, setValidated] = useState(false);
  const [supplierInstallments, setCustomerInstallments] = useState([]);
  const [loadingInstallments, setLoadingInstallments] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (initialValues) {
      setFormData(initialValues);
      
      // If there's a supplier ID in the initial values, fetch their installments
      if (initialValues.supplier) {
        fetchCustomerInstallments(initialValues.supplier);
      }
      
      // If there's an installment ID, set it as selected
      if (initialValues.installment) {
        setFormData({
          ...initialValues,
          // Ensure numeric values are handled correctly
          amount: initialValues.amount ? initialValues.amount.toString() : '',
        });
      }
    } else {
      // Default values
      setFormData({
        supplier: '',
        installment: '',
        payment_method: 'كاش',
        amount: '',
        notes: ''
      });
    }
    
    // Reset error when modal is opened/closed
    setError(null);
  }, [initialValues, show]);
  
  useEffect(() => {
    // If we have installments and there's a selected installment ID in the form data
    if (supplierInstallments.length > 0 && formData.installment) {
      const selectedInst = supplierInstallments.find(
        inst => inst.id.toString() === formData.installment.toString()
      );
      
      if (selectedInst) {
        setSelectedInstallment(selectedInst);
      }
    }
  }, [supplierInstallments, formData.installment]);
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'supplier' && value) {
      // When supplier changes, fetch their installments
      fetchCustomerInstallments(value);
      
      // Reset installment selection when supplier changes
      setFormData({
        ...formData,
        supplier: value,
        installment: '',
        amount: ''
      });
    } else if (name === 'installment' && value) {
      // When installment is selected, find the installment details
      const selected = supplierInstallments.find(inst => inst.id.toString() === value.toString());
      setSelectedInstallment(selected);
      
      // Pre-fill amount with the installment amount and make it non-editable
      setFormData({
        ...formData,
        installment: value,
        amount: selected ? selected.amount : '',
        notes: selected ? `دفع القسط رقم ${selected.installment_number}` : formData.notes
      });
    } else {
      // Only update amount if no installment is selected
      if (name === 'amount' && formData.installment) {
        return; // not editable
      }
      
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const fetchCustomerInstallments = async (supplierId) => {
    setLoadingInstallments(true);
    setError(null);
    try {
      // Use apiClient instead of fetch
      const response = await apiClient.get('/transactions/installments/');
      
      // Process the data based on the JSON format you provided
      const data = response.data;
      
      // Filter installments for the selected supplier that are not paid
      const filteredInstallments = data.installments.filter(
        inst => inst.supplier === suppliers.find(c => c.id.toString() === supplierId.toString())?.name && !inst.is_paid
      );
      
      setCustomerInstallments(filteredInstallments);
    } catch (error) {
      console.error('Error fetching installments:', error);
      setError('خطأ في تحميل الأقساط. يرجى المحاولة مرة أخرى.');
      setCustomerInstallments([]);
    } finally {
      setLoadingInstallments(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    // Add the selected installment ID to the form data
    const submissionData = {
      ...formData,
      installment_id: formData.installment || null
    };

    try {
      await handleSubmit(submissionData);
      handleClose();
    } catch (error) {
      // If the handleSubmit function throws an error, catch it here
      if (error.response) {
        setError(error.response.data.detail || 'حدث خطأ أثناء معالجة الطلب');
      } else {
        setError('حدث خطأ أثناء الاتصال بالسيرفر');
      }
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={handleClose}
      centered
      size="lg"
      dir="rtl"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {mode === 'add' ? 'إضافة دفعة جديدة' : 'تعديل بيانات الدفعة'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Display error message if exists */}
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}
        
        <Form noValidate validated={validated} onSubmit={onSubmit}>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="formCustomer">
              <Form.Label> المورد*</Form.Label>
              <Form.Select 
                name="supplier"
                value={formData.supplier}
                onChange={handleChange}
                required
              >
                <option value="">اختر المورد</option>
                {suppliers.map(supplier => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                يرجى اختيار المورد
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="formInstallment">
              <Form.Label>اختر القسط</Form.Label>
              {loadingInstallments ? (
                <div className="text-center py-2">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : (
                <Form.Select 
                  name="installment"
                  value={formData.installment}
                  onChange={handleChange}
                  disabled={!formData.supplier || supplierInstallments.length === 0}
                >
                  <option value="">اختر القسط المراد دفعه</option>
                  {supplierInstallments.map(installment => (
                    <option key={installment.id} value={installment.id}>
                      قسط رقم {installment.installment_number} - {installment.amount} - تاريخ الاستحقاق: {installment.due_date}
                    </option>
                  ))}
                </Form.Select>
              )}
              {formData.supplier && supplierInstallments.length === 0 && !loadingInstallments && (
                <Form.Text className="text-muted">
                  لا توجد أقساط غير مدفوعة لهذا المورد
                </Form.Text>
              )}
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md={6} controlId="formAmount">
              <Form.Label>المبلغ*</Form.Label>
              <Form.Control
                type="number"
                placeholder="أدخل المبلغ"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                required
                min="0.01"
                step="0.01"
                readOnly={!!formData.installment} 
                disabled={!!formData.installment} 
              />
              <Form.Control.Feedback type="invalid">
                يرجى إدخال مبلغ صحيح
              </Form.Control.Feedback>
              {formData.installment && (
                <Form.Text className="text-muted">
                  المبلغ محدد تلقائياً بناءً على قيمة القسط المختار
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group as={Col} md={6} controlId="formPaymentMethod">
              <Form.Label>طريقة الدفع*</Form.Label>
              <Form.Select 
                name="payment_method"
                value={formData.payment_method}
                onChange={handleChange}
                required
              >
                <option value="">اختر طريقة الدفع</option>
                {paymentMethods.filter(method => method.is_supplier_payment === true).map(method => (
                  <option key={method.value} value={method.value}>
                    {method.arabic_name}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                يرجى اختيار طريقة الدفع
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Form.Group className="mb-3" controlId="formNotes">
            <Form.Label>ملاحظات</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="أدخل أي ملاحظات إضافية"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
            />
          </Form.Group>

          <div className="d-flex justify-content-end">
            <Button variant="secondary" onClick={handleClose} className="me-2">
              إلغاء
            </Button>
            <Button variant="primary" type="submit">
              {mode === 'add' ? 'إضافة' : 'تحديث'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default PaymentModal;